import HyperDX from '@hyperdx/browser';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-final-form';
import { AnalyticsContext, isInIframe, reportAction } from 'shared';
const EVENT_NAME = 'FormAbandoned';
const FORM_NAME = 'prequal_personal_info';
const NO_FIELD_LABEL = '(none)';
const FIELD_LABELS = {
    confirmPrefilledInfo: 'Confirm Prefilled Info',
    firstName: 'First Name',
    lastName: 'Last Name',
    street1: 'Street 1',
    street2: 'Street 2',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    dateOfBirth: 'Date of Birth',
    ssn: 'SSN/ITIN',
    email: 'Email Address',
    annualIncome: 'Annual Income',
    purchaseAmount: 'Estimated Cost of Treatment',
    specialty: 'Specialty',
    primaryPhone: 'Mobile Number',
    alternatePhone: 'Home Number',
    homeNumberCheck: 'Provide Alternate Phone',
    agreedAlphaeon: 'Alphaeon Terms',
    agreedComenity: 'Comenity Terms',
};
const isEmptyValue = (value) => value == null ||
    value === false ||
    (typeof value === 'string' && value.trim() === '');
/**
 * Reports the field an applicant most recently filled in when they leave the
 * prequalification form without submitting it. Renders nothing.
 *
 * Nothing distinguishes leaving for good from leaving for a moment, so this
 * fires whenever the applicant goes away and reports how far they had got.
 * Coming back and filling more sends a fresh event superseding the last, so
 * abandonment is the final event of a session that never reported a submission.
 *
 * Reports through gtag when the page is unloading and through the analytics
 * client otherwise, and sends the same event to HyperDX with the full list of
 * fields the applicant filled.
 */
const FormAbandonmentTracker = ({ isComplete, prefillSource, applicationId, }) => {
    const form = useForm('FormAbandonmentTracker');
    const { analytics } = useContext(AnalyticsContext);
    const mountedAtRef = useRef(Date.now());
    const fillSequenceRef = useRef([]);
    const hasRenderedFieldsRef = useRef(false);
    const lastReportedRef = useRef(null);
    const updateFillSequence = useCallback(() => {
        const registered = form.getRegisteredFields();
        if (registered.length === 0)
            return;
        hasRenderedFieldsRef.current = true;
        const { values } = form.getState();
        const userFilled = registered.filter((name) => {
            const fieldState = form.getFieldState(name);
            if (!isEmptyValue(fieldState?.initial))
                return false;
            return !isEmptyValue(values[name]);
        });
        fillSequenceRef.current = [
            ...fillSequenceRef.current.filter((name) => userFilled.includes(name)),
            ...userFilled.filter((name) => !fillSequenceRef.current.includes(name)),
        ];
    }, [form]);
    const latestRef = useRef({
        analytics,
        applicationId,
        isComplete,
        prefillSource,
        updateFillSequence,
    });
    latestRef.current = {
        analytics,
        applicationId,
        isComplete,
        prefillSource,
        updateFillSequence,
    };
    useEffect(() => form.subscribe(updateFillSequence, { values: true }), [form, updateFillSequence]);
    useEffect(() => {
        const report = (isPageUnloading) => {
            const current = latestRef.current;
            current.updateFillSequence();
            if (current.isComplete || !hasRenderedFieldsRef.current)
                return;
            const lastField = fillSequenceRef.current[fillSequenceRef.current.length - 1] ?? '';
            if (lastField === lastReportedRef.current)
                return;
            lastReportedRef.current = lastField;
            const hyperdxSessionId = HyperDX.getSessionId();
            const payload = {
                form_name: FORM_NAME,
                last_field_label: lastField
                    ? FIELD_LABELS[lastField] ?? lastField
                    : NO_FIELD_LABEL,
                last_field_name: lastField,
                seconds_on_form: Math.round((Date.now() - mountedAtRef.current) / 1000),
                prefill_source: current.prefillSource ?? 'none',
                is_embedded: isInIframe(),
                ...(current.applicationId
                    ? { credit_application_id: current.applicationId }
                    : {}),
                ...(hyperdxSessionId ? { hyperdx_session_id: hyperdxSessionId } : {}),
            };
            if (isPageUnloading && window.gtag) {
                window.gtag('event', EVENT_NAME, payload);
            }
            else {
                current.analytics?.track(EVENT_NAME, payload);
            }
            reportAction(EVENT_NAME, {
                componentName: 'FormAbandonmentTracker',
                actionType: EVENT_NAME,
                actionURL: window.location.pathname,
                actionData: { ...payload, fields_filled: fillSequenceRef.current },
            });
        };
        const handleVisibilityChange = () => {
            if (document.hidden)
                report(true);
        };
        const handlePageHide = () => report(true);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            report(false);
        };
    }, []);
    return null;
};
export default FormAbandonmentTracker;
